import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Save, Plus, Trash2, Paintbrush, Pen, Camera, Frame,
  Sparkles, DollarSign, RefreshCw, X, Image, Tag, FolderOpen, ZoomIn, Library
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to add cache busting to thumbnail URLs
const getThumbUrl = (url, updatedAt) => {
  if (!url) return null;
  // Add timestamp query param for cache busting
  const cacheBuster = updatedAt ? new Date(updatedAt).getTime() : Date.now();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${cacheBuster}`;
};

const AdminArtwork = () => {
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const emptyItem = {
    title: '',
    description: '',
    category: 'pen-ink',
    medium: '',
    dimensions: '',
    year_created: new Date().getFullYear().toString(),
    image_url: '',
    thumbnail_url: '',
    price: 0,
    original_price: null,
    is_original: true,
    is_sold: false,
    is_featured: false,
    stock: 1,
    tags: [],
    order: 0,
    // Print options
    prints_available: false,
    print_price_percentages: { small: 15, medium: 25, large: 40 }
  };

  // Print size configurations
  const PRINT_SIZES = {
    small: { name: 'Small (8×10")', dimensions: '8×10 inches' },
    medium: { name: 'Medium (11×14")', dimensions: '11×14 inches' },
    large: { name: 'Large (16×20")', dimensions: '16×20 inches' }
  };

  const [formData, setFormData] = useState(emptyItem);
  const [tagsInput, setTagsInput] = useState('');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null); // 'image' or 'thumbnail'

  useEffect(() => {
    fetchArtwork();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchArtwork = async () => {
    try {
      const res = await fetch(`${API}/api/artwork?available_only=false`);
      const data = await res.json();
      setArtwork(data);
    } catch (err) {
      console.error('Failed to fetch artwork:', err);
      toast.error('Failed to load artwork');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.image_url || !formData.price) {
      toast.error('Title, image URL, and price are required');
      return;
    }

    setSaving(true);
    try {
      // Convert tags from comma-separated string to array
      const dataToSave = {
        ...formData,
        tags: tagsInput.split(',').map(t => t.trim()).filter(t => t),
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock) || 1
      };

      const url = editingItem 
        ? `${API}/api/artwork/${editingItem.id}`
        : `${API}/api/artwork`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(dataToSave)
      });

      if (!res.ok) {
        if (res.status === 401) {
          toast.error('Session expired. Please log in again.');
          return;
        }
        throw new Error('Failed to save');
      }

      toast.success(editingItem ? 'Artwork updated!' : 'Artwork added!');
      fetchArtwork();
      resetForm();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save artwork');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this artwork?')) return;

    try {
      const res = await fetch(`${API}/api/artwork/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        if (res.status === 401) {
          toast.error('Session expired. Please log in again.');
          return;
        }
        throw new Error('Failed to delete');
      }

      toast.success('Artwork deleted!');
      fetchArtwork();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete artwork');
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing artwork with sample data. Continue?')) return;

    try {
      const res = await fetch(`${API}/api/artwork/seed`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Failed to seed data');

      toast.success('Sample artwork added!');
      fetchArtwork();
    } catch (err) {
      console.error('Seed error:', err);
      toast.error('Failed to seed data');
    }
  };

  const resetForm = () => {
    setFormData(emptyItem);
    setTagsInput('');
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'pen-ink',
      medium: item.medium || '',
      dimensions: item.dimensions || '',
      year_created: item.year_created || '',
      image_url: item.image_url || '',
      thumbnail_url: item.thumbnail_url || '',
      price: item.price || 0,
      original_price: item.original_price || null,
      is_original: item.is_original ?? true,
      is_sold: item.is_sold ?? false,
      is_featured: item.is_featured ?? false,
      stock: item.stock || 1,
      tags: item.tags || [],
      order: item.order || 0,
      // Print options
      prints_available: item.prints_available ?? false,
      print_price_percentages: item.print_price_percentages || { small: 15, medium: 25, large: 40 }
    });
    setTagsInput((item.tags || []).join(', '));
    setEditingItem(item);
    setShowForm(true);
  };

  const toggleSold = async (item) => {
    try {
      const res = await fetch(`${API}/api/artwork/${item.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_sold: !item.is_sold })
      });

      if (!res.ok) throw new Error('Failed to update');

      toast.success(item.is_sold ? 'Marked as available' : 'Marked as sold');
      fetchArtwork();
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update');
    }
  };

  const categoryIcons = {
    'pen-ink': Pen,
    'painting': Paintbrush,
    'print': Frame,
    'original': Sparkles,
    'photograph': Camera,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Paintbrush className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Artwork Editor</h1>
              <p className="text-sm text-muted-foreground">Manage your artwork gallery</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSeedData}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Load Sample Data
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-black rounded-lg flex items-center gap-2 font-bold transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Artwork
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                {editingItem ? 'Edit Artwork' : 'Add New Artwork'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    placeholder="Carnival Rhythm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground h-24"
                    placeholder="A vibrant pen and ink drawing..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      <option value="pen-ink">Pen & Ink</option>
                      <option value="painting">Painting</option>
                      <option value="print">Print</option>
                      <option value="original">Original</option>
                      <option value="photograph">Photograph</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Year Created</label>
                    <input
                      type="text"
                      value={formData.year_created}
                      onChange={(e) => setFormData({ ...formData, year_created: e.target.value })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      placeholder="2024"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Medium</label>
                  <input
                    type="text"
                    value={formData.medium}
                    onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    placeholder="Pen & Ink on Archival Paper"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Dimensions</label>
                  <input
                    type="text"
                    value={formData.dimensions}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    placeholder="11 x 14 inches"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Image URL *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      placeholder="https://... or select from library"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMediaPickerTarget('image');
                        setShowMediaPicker(true);
                      }}
                      className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Browse
                    </button>
                  </div>
                  
                  {/* Upload to Artwork Folder OR Browse Library */}
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowMediaPicker(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-lg transition-colors"
                    >
                      <Library className="w-4 h-4" />
                      Browse Library
                    </button>
                    <label className="flex items-center gap-2 px-4 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg cursor-pointer transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          try {
                            toast.loading('Uploading & generating prints...', { id: 'upload-art' });
                            const formDataUpload = new FormData();
                            formDataUpload.append('file', file);
                            formDataUpload.append('title', formData.title || file.name);
                            
                            const res = await fetch(`${API}/api/artwork-prints/upload`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                              },
                              body: formDataUpload
                            });
                            
                            if (!res.ok) throw new Error('Upload failed');
                            const data = await res.json();
                            
                            setFormData(prev => ({
                              ...prev,
                              image_url: data.original_url,
                              prints_available: true
                            }));
                            
                            toast.success(`Uploaded! ${Object.keys(data.print_files).length} print sizes generated`, { id: 'upload-art' });
                          } catch (err) {
                            console.error(err);
                            toast.error('Failed to upload artwork', { id: 'upload-art' });
                          }
                          e.target.value = '';
                        }}
                      />
                      <Plus className="w-4 h-4" />
                      Upload to Artwork Folder (Auto-generates prints)
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="mt-2 relative w-32 h-32 rounded-lg overflow-hidden bg-background border border-border">
                      <img loading="lazy" decoding="async" src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Thumbnail URL (optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      placeholder="https://... or select from library"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMediaPickerTarget('thumbnail');
                        setShowMediaPicker(true);
                      }}
                      className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Browse
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Price *</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full pl-8 pr-4 py-2 bg-background border border-border rounded-lg text-foreground"
                        placeholder="250.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Original Price</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="number"
                        value={formData.original_price || ''}
                        onChange={(e) => setFormData({ ...formData, original_price: e.target.value || null })}
                        className="w-full pl-8 pr-4 py-2 bg-background border border-border rounded-lg text-foreground"
                        placeholder="For sales"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Stock</label>
                    <input
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                    placeholder="carnival, dancers, pen-ink"
                  />
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_original}
                      onChange={(e) => setFormData({ ...formData, is_original: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-foreground">Original (One-of-a-kind)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-foreground">Featured</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_sold}
                      onChange={(e) => setFormData({ ...formData, is_sold: e.target.checked })}
                      className="w-4 h-4 accent-red-500"
                    />
                    <span className="text-sm text-foreground">Sold</span>
                  </label>
                </div>

                {/* Print Options Section */}
                <div className="border-t border-border/30 pt-4 mt-2">
                  <div className="flex items-center gap-2 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.prints_available}
                        onChange={(e) => setFormData({ ...formData, prints_available: e.target.checked })}
                        className="w-4 h-4 accent-green-500"
                      />
                      <span className="text-sm font-medium text-foreground">Digital Prints Available</span>
                    </label>
                  </div>

                  {formData.prints_available && (
                    <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                      <p className="text-xs text-muted-foreground mb-3">
                        Print prices are calculated as a percentage of the original price (${formData.price || 0})
                      </p>
                      
                      {Object.entries(PRINT_SIZES).map(([key, size]) => {
                        const percent = formData.print_price_percentages?.[key] || 
                          (key === 'small' ? 15 : key === 'medium' ? 25 : 40);
                        const calculatedPrice = ((formData.price || 0) * percent / 100).toFixed(2);
                        
                        return (
                          <div key={key} className="flex items-center gap-4">
                            <div className="flex-1">
                              <span className="text-sm text-foreground">{size.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">({size.dimensions})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={percent}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  print_price_percentages: {
                                    ...formData.print_price_percentages,
                                    [key]: parseInt(e.target.value) || 0
                                  }
                                })}
                                className="w-16 px-2 py-1 bg-background border border-border rounded text-foreground text-sm text-center"
                                min="1"
                                max="100"
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                              <span className="text-sm text-green-400 font-medium w-20 text-right">
                                ${calculatedPrice}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      
                      <p className="text-xs text-amber-400/80 mt-2">
                        Customers can download print-ready files after purchase (3 downloads, 2-day expiry)
                      </p>
                      
                      {/* Generate Prints Button */}
                      {editingItem && (
                        <button
                          onClick={async () => {
                            try {
                              toast.loading('Generating print files...', { id: 'gen-prints' });
                              const res = await fetch(`${API}/api/artwork-prints/generate/${editingItem.id}`, {
                                method: 'POST',
                                headers: getAuthHeaders()
                              });
                              if (!res.ok) throw new Error('Failed to generate');
                              const data = await res.json();
                              toast.success(`Generated ${Object.keys(data.prints || {}).length} print sizes!`, { id: 'gen-prints' });
                            } catch (err) {
                              toast.error('Failed to generate prints', { id: 'gen-prints' });
                            }
                          }}
                          className="mt-3 w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Generate Print Files
                        </button>
                      )}
                      
                      {/* Upscale & Generate Button */}
                      {editingItem && (
                        <button
                          onClick={async () => {
                            try {
                              toast.loading('Upscaling image & generating prints...', { id: 'upscale' });
                              const res = await fetch(`${API}/api/artwork-prints/upscale/${editingItem.id}`, {
                                method: 'POST',
                                headers: getAuthHeaders()
                              });
                              const data = await res.json();
                              if (!res.ok) {
                                throw new Error(data.detail || 'Failed to upscale');
                              }
                              toast.success(
                                `Upscaled to ${data.upscale_result?.final_size?.[0]}×${data.upscale_result?.final_size?.[1]}px! ${data.print_files_generated} print sizes generated.`, 
                                { id: 'upscale', duration: 5000 }
                              );
                              fetchArtwork(); // Refresh data
                            } catch (err) {
                              toast.error(err.message || 'Failed to upscale', { id: 'upscale' });
                            }
                          }}
                          className="mt-2 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                        >
                          <ZoomIn className="w-4 h-4" />
                          Upscale & Generate Prints (AI Enhanced)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border/30">
              <button
                onClick={resetForm}
                className="px-6 py-2 border border-border rounded-lg text-foreground hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-primary hover:bg-primary/90 text-black rounded-lg font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                    <RefreshCw className="w-4 h-4" />
                  </motion.div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Artwork
              </button>
            </div>
          </motion.div>
        )}

        {/* Artwork List */}
        <div className="grid gap-4">
          {artwork.length === 0 ? (
            <div className="bg-card border border-border/50 rounded-2xl p-12 text-center">
              <Paintbrush className="w-16 h-16 mx-auto mb-4 text-primary/30" />
              <h3 className="text-xl font-bold text-foreground mb-2">No Artwork Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add your first artwork or load sample data to get started.
              </p>
              <button
                onClick={handleSeedData}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Load Sample Data
              </button>
            </div>
          ) : (
            artwork.map((item) => {
              const Icon = categoryIcons[item.category] || Image;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`bg-card border border-border/50 rounded-xl p-4 flex items-center gap-4 ${
                    item.is_sold ? 'opacity-60' : ''
                  }`}
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-background flex-shrink-0">
                    {item.image_url ? (
                      <img loading="lazy" decoding="async" 
                        src={getThumbUrl(item.thumbnail_url || item.image_url, item.updated_at)} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-primary/30" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-foreground truncate">{item.title}</h3>
                      {item.is_original && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Original
                        </span>
                      )}
                      {item.is_featured && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                          Featured
                        </span>
                      )}
                      {item.is_sold && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                          Sold
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="capitalize flex items-center gap-1">
                        <Icon className="w-4 h-4" />
                        {item.category.replace('-', ' ')}
                      </span>
                      <span className="text-primary font-bold">${item.price?.toFixed(2)}</span>
                      {item.stock > 1 && <span>{item.stock} in stock</span>}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleSold(item)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        item.is_sold 
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}
                    >
                      {item.is_sold ? 'Mark Available' : 'Mark Sold'}
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-foreground transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* Media Library Picker Modal */}
      <MediaLibraryPicker
        isOpen={showMediaPicker}
        onClose={() => {
          setShowMediaPicker(false);
          setMediaPickerTarget(null);
        }}
        onSelect={(selected) => {
          // After the MediaLibraryPicker single-pick fix, `selected` is a
          // single media object (not an array).
          const picked = Array.isArray(selected) ? selected[0] : selected;
          if (picked?.url) {
            const mediaUrl = picked.url;
            if (mediaPickerTarget === 'image') {
              setFormData({ ...formData, image_url: mediaUrl });
            } else if (mediaPickerTarget === 'thumbnail') {
              setFormData({ ...formData, thumbnail_url: mediaUrl });
            }
          }
          setShowMediaPicker(false);
          setMediaPickerTarget(null);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

export default AdminArtwork;
